import axios from '@/axiosConnection'
import toastr from '@/toastr'

const API = 'locationforaccount'

// Code couleur des statuts, avec les variantes de badge du reste du back-office. La couleur
// dit qui doit agir : gris tant que personne n'a pris la demande, ambre quand la main est
// au demandeur, vert plein quand le matériel est chez le client et que la facturation court.
// Les états clos s'éteignent, sauf le refus. (Dans ce thème, `secondary` et `light` rendent
// le même gris : d'où un seul gris pour « pas encore pris » et « clos ».)
const STATUS_VARIANTS = {
  'location-for-account-soumise': 'light',
  'location-for-account-en-traitement': 'info',
  'location-for-account-chiffree': 'warning',
  'location-for-account-acceptee': 'success',
  'location-for-account-en-location': 'primary',
  'location-for-account-terminee': 'light',
  'location-for-account-refusee': 'danger',
  'location-for-account-annulee': 'light'
}

/**
 * Demandes de location pour compte, côté back-office.
 *
 * Les actions de workflow ne sont pas des `saveItem` : chacune correspond à une transition
 * portée par le manager Symfony, qui refuse ce que la table des transitions n'autorise pas.
 * Une transition interdite revient en 422 avec son message, que l'écran affiche tel quel —
 * c'est une erreur de saisie, pas une panne.
 */
export default {
  namespaced: true,
  state: {
    items: [],
    detail: null,
    // Matériels de nature LPC, pour le filtre de la liste. Ils ont leur propre source :
    // `/product` les exclut désormais, ils ne vivent que dans « Produits spéciaux ».
    products: [],
    options: {
      statuses: {},
      statusOrder: [],
      feeCategories: {},
      feeStates: {},
      // Seuil « arrive à terme », administrable. Défaut serveur : 2 jours.
      endAlertDelay: 2
    }
  },
  getters: {
    getAll: state => state.items,
    getItem: state => id => state.items.find(item => item.id == id),
    getDetail: state => state.detail,
    getStatusLabel: state => slug => (state.options.statuses || {})[slug] || slug,
    getStatusVariant: () => slug => STATUS_VARIANTS[slug] || 'light',
    getStatusTabs: state => (state.options.statusOrder || []).map(slug => ({
      value: slug,
      text: (state.options.statuses || {})[slug] || slug
    })),
    getFeeCategoriesForSelect: state => Object.entries(state.options.feeCategories || {})
      .map(([value, text]) => ({ value, text })),
    getFeeCategoryLabel: state => category => (state.options.feeCategories || {})[category] || category,
    getFeeStateLabel: state => value => (state.options.feeStates || {})[value] || value,
    getEndAlertDelay: state => state.options.endAlertDelay,
    getProductsForSelect: state => state.products.map(product => ({
      value: product.id,
      text: product.internal_code ? product.internal_code + ' — ' + product.name : product.name
    }))
  },
  mutations: {
    setOptions(state, options) {
      state.options = { ...state.options, ...options }
    },
    setDetail(state, detail) {
      state.detail = detail
    },
    setProducts(state, products) {
      state.products = products
    }
  },
  actions: {
    getOptions(context) {
      return axios.get(API + '/options')
        .then(response => {
          if (response && response.data) {
            context.commit('setOptions', response.data)
          }
          return response
        })
        .catch(error => {
          toastr.error("Le chargement des options a échoué")
          return Promise.reject(error)
        })
    },
    /** Matériels sur lesquels une demande peut porter : la nature LPC, et elle seule. */
    getProducts(context) {
      return axios.get('product', { params: { parents: true, type: 'location_for_account', limit: 0 } })
        .then(response => {
          context.commit('setProducts', (response.data && response.data.products) || [])
          return response
        })
        .catch(() => context.commit('setProducts', []))
    },
    /** Détail complet d'une demande : chiffrage, frais, historique, sollicitations en attente. */
    getDetail(context, id) {
      return axios.get(API + '/' + id)
        .then(response => {
          context.commit('setDetail', response.data)
          return response
        })
        .catch(error => {
          toastr.error("Le chargement de la demande a échoué")
          return Promise.reject(error)
        })
    },
    /**
     * Exécute une transition. Le message d'erreur du 422 vient du manager : il dit pourquoi
     * la règle a bloqué (« la facturation a démarré », « transition interdite »), c'est lui
     * qu'on montre, pas un message générique.
     */
    action(context, { id, action, payload = {} }) {
      // Une action peut porter un fichier — le bon de commande de l'acceptation. Dans ce cas
      // l'envoi passe en multipart : un objet JSON ne transporte pas de fichier.
      const hasFile = Object.values(payload).some(value => value instanceof File)
      let body = payload
      if (hasFile) {
        body = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            body.append(key, value)
          }
        })
      }

      return axios.post(API + '/' + id + '/' + action, body)
        .then(response => {
          context.dispatch('getDetail', id)
          return response
        })
        .catch(error => {
          const errors = error.response && error.response.data && error.response.data.errors
          toastr.error(errors && errors.length ? errors[0] : "L'action a échoué")
          return Promise.reject(error)
        })
    },
    addFee(context, { id, fee }) {
      return axios.post(API + '/' + id + '/fees', fee)
        .then(response => {
          context.dispatch('getDetail', id)
          return response
        })
        .catch(error => {
          const errors = error.response && error.response.data && error.response.data.errors
          toastr.error(errors && errors.length ? errors[0] : "L'ajout de la ligne de frais a échoué")
          return Promise.reject(error)
        })
    },
    saveFee(context, { id, feeId, fee }) {
      return axios.patch(API + '/' + id + '/fees/' + feeId, fee)
        .then(response => {
          context.dispatch('getDetail', id)
          return response
        })
        .catch(error => {
          const errors = error.response && error.response.data && error.response.data.errors
          toastr.error(errors && errors.length ? errors[0] : "La modification a échoué")
          return Promise.reject(error)
        })
    },
    deleteFee(context, { id, feeId }) {
      return axios.delete(API + '/' + id + '/fees/' + feeId)
        .then(response => {
          context.dispatch('getDetail', id)
          return response
        })
        .catch(error => {
          const errors = error.response && error.response.data && error.response.data.errors
          toastr.error(errors && errors.length ? errors[0] : "La suppression a échoué")
          return Promise.reject(error)
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/modules/location_for_account.js